import AutoLoadingButton from "@/components/AutoLoadingButton";
import DraggableList from "@/components/DraggableList";
import { useOrderSetting } from "@/hooks/useOrderSetting";
import { UniversalOrderSettingType } from "@/services/api/universalOrderSetting";
import { collectEvent } from "@/services/dataTrack";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import { useGlobalStore } from "@/store/hooks";
import { isEmpty } from "@/utils";
import { Button, Input, Popover, Spin, Tag } from "antd";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import "./index.less";

interface FrequentRemarkListProps {
    onRemarkTagClick: (remark: string) => void;
}

interface FrequentRemark {
    id: number;
    content: string;
}

const FrequentRemarkList: React.FC<FrequentRemarkListProps> = ({ onRemarkTagClick }) => {
    const globalStore = useGlobalStore();
    const [popoverVisible, setPopoverVisible] = useState(false);
    const [remark, setRemark] = useState("");
    const companyId = globalStore.companyId;

    const {
        data: frequentRemarkList,
        save,
        loading,
    } = useOrderSetting<FrequentRemark[]>({
        type: UniversalOrderSettingType.ORDER_FLAG_LABEL,
        companyId,
        defaultValue: [],
    });

    /** 添加备注 */
    const handleAddRemark = async () => {
        if (isEmpty(remark)) return;

        const newList = [...frequentRemarkList, { id: Date.now(), content: remark.trim() }];

        await save(newList);

        setRemark("");
        setPopoverVisible(false);
    };

    /** 删除备注 */
    const handleDelete = async (item: FrequentRemark) => {
        const newList = frequentRemarkList.filter((r) => r.id !== item.id);
        await save(newList);
    };

    /** 拖拽更新 */
    const handleDragEnd = async (newList: FrequentRemark[]) => {
        await save(newList);
    };

    return (
        <Spin spinning={loading}>
            <div className="flex flex-wrap frequent-remark-list">
                <div className="flex flex-center flex-wrap">
                    <DraggableList
                        data={frequentRemarkList}
                        itemKey="id"
                        renderItem={(item, { ref, opacity, isDraggingGlobal }) => (
                            <Popover key={item.id} content={item.content} open={isDraggingGlobal ? false : undefined}>
                                <Tag
                                    variant="outlined"
                                    ref={ref}
                                    className="cursor-pointer"
                                    closable
                                    onClose={() => handleDelete(item)}
                                    style={{
                                        opacity,
                                        display: "flex",
                                        alignItems: "center",
                                        marginTop: "3px",
                                        marginRight: 8,
                                    }}
                                    onClick={() => {
                                        onRemarkTagClick(item.content);

                                        collectEvent(DataTrackEvent.CLICK_FREQUENT_REMARK, {
                                            content: item.content,
                                            company_id: companyId,
                                        });
                                    }}
                                >
                                    <span
                                        style={{
                                            display: "inline-block",
                                            maxWidth: "100px",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {item.content}
                                    </span>
                                </Tag>
                            </Popover>
                        )}
                        onDragEnd={handleDragEnd}
                    />
                </div>

                <div>
                    <Popover
                        title="添加常用备注"
                        content={
                            <div>
                                <Input
                                    size="small"
                                    value={remark}
                                    onChange={(e) => setRemark(e.target.value)}
                                    placeholder="请输入备注"
                                    autoFocus
                                />
                                <div className="mt10 tr">
                                    <Button onClick={() => setPopoverVisible(false)} size="small">
                                        取消
                                    </Button>
                                    <AutoLoadingButton
                                        className="ml5"
                                        type="primary"
                                        onClick={handleAddRemark}
                                        disabled={!remark}
                                        size="small"
                                    >
                                        添加
                                    </AutoLoadingButton>
                                </div>
                            </div>
                        }
                        trigger="click"
                        open={popoverVisible}
                        onOpenChange={setPopoverVisible}
                    >
                        <Button type="link" size="small" className="fs12 mt5">
                            添加常用备注
                        </Button>
                    </Popover>

                    {frequentRemarkList && frequentRemarkList.length === 0 ? (
                        <span className="ml3 text-gray small">(建议常用商品、客户要求、快递信息等​​设为常用备注)</span>
                    ) : null}
                </div>
            </div>
        </Spin>
    );
};

export default observer(FrequentRemarkList);
